import { authFetch } from '../../utils/authFetch';
// src/redux/actions/globalConfigActions.js
// Redux actions for managing global configs from Supabase

// Action types
export const FETCH_GLOBAL_CONFIG_START = 'FETCH_GLOBAL_CONFIG_START';
export const FETCH_GLOBAL_CONFIG_SUCCESS = 'FETCH_GLOBAL_CONFIG_SUCCESS';
export const FETCH_GLOBAL_CONFIG_FAILURE = 'FETCH_GLOBAL_CONFIG_FAILURE';

export const SAVE_GLOBAL_CONFIG_START = 'SAVE_GLOBAL_CONFIG_START';
export const SAVE_GLOBAL_CONFIG_SUCCESS = 'SAVE_GLOBAL_CONFIG_SUCCESS';
export const SAVE_GLOBAL_CONFIG_FAILURE = 'SAVE_GLOBAL_CONFIG_FAILURE';

export const FETCH_ALL_GLOBAL_CONFIGS_START = 'FETCH_ALL_GLOBAL_CONFIGS_START';
export const FETCH_ALL_GLOBAL_CONFIGS_SUCCESS = 'FETCH_ALL_GLOBAL_CONFIGS_SUCCESS';
export const FETCH_ALL_GLOBAL_CONFIGS_FAILURE = 'FETCH_ALL_GLOBAL_CONFIGS_FAILURE';

/**
 * Fetch a single global config by key
 * @param {string} configKey - Config key (e.g., 'modelFormConfig')
 * @returns {Function} Thunk action
 */
export const fetchGlobalConfig = (configKey) => async (dispatch, getState) => {
  // Check if already loaded
  const existing = getState().globalConfigs?.configs?.[configKey];
  if (existing) {
    console.log(`[globalConfigActions] Config '${configKey}' already loaded`);
    return existing;
  }

  dispatch({ type: FETCH_GLOBAL_CONFIG_START, payload: { configKey } });

  try {
    console.log(`[globalConfigActions] Fetching global config: ${configKey}`);

    const response = await authFetch('/.netlify/functions/global-configs-get', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ configKey })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch global config');
    }

    const data = await response.json();

    console.log(`[globalConfigActions] Global config '${configKey}' fetched successfully`);

    dispatch({
      type: FETCH_GLOBAL_CONFIG_SUCCESS,
      payload: {
        configKey,
        configData: data.configData,
        description: data.description
      }
    });

    return data.configData;
  } catch (error) {
    console.error(`[globalConfigActions] Error fetching global config '${configKey}':`, error);
    dispatch({
      type: FETCH_GLOBAL_CONFIG_FAILURE,
      payload: { configKey, error: error.message }
    });
    throw error;
  }
};

/**
 * Save/update a global config
 * @param {string} configKey - Config key
 * @param {Object} configData - Config data to save
 * @param {string} description - Optional description
 * @returns {Function} Thunk action
 */
export const saveGlobalConfig = (configKey, configData, description) => async (dispatch) => {
  dispatch({ type: SAVE_GLOBAL_CONFIG_START, payload: { configKey } });

  try {
    console.log(`[globalConfigActions] Saving global config: ${configKey}`);

    const response = await authFetch('/.netlify/functions/global-configs-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ configKey, configData, description })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save global config');
    }

    console.log(`[globalConfigActions] Global config '${configKey}' saved successfully`);

    dispatch({
      type: SAVE_GLOBAL_CONFIG_SUCCESS,
      payload: { configKey, configData, description }
    });

    // Refetch to get updated data
    dispatch(fetchGlobalConfig(configKey));

    return { success: true };
  } catch (error) {
    console.error(`[globalConfigActions] Error saving global config '${configKey}':`, error);
    dispatch({
      type: SAVE_GLOBAL_CONFIG_FAILURE,
      payload: { configKey, error: error.message }
    });
    throw error;
  }
};

/**
 * Fetch all available global configs (metadata only)
 * @returns {Function} Thunk action
 */
export const fetchAllGlobalConfigs = () => async (dispatch) => {
  dispatch({ type: FETCH_ALL_GLOBAL_CONFIGS_START });

  try {
    console.log('[globalConfigActions] Fetching all global configs');

    const response = await authFetch('/.netlify/functions/global-configs-list', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch global configs list');
    }

    const data = await response.json();

    console.log(`[globalConfigActions] Fetched ${data.count} global configs`);

    dispatch({
      type: FETCH_ALL_GLOBAL_CONFIGS_SUCCESS,
      payload: data.configs || []
    });

    return data.configs;
  } catch (error) {
    console.error('[globalConfigActions] Error fetching global configs list:', error);
    dispatch({
      type: FETCH_ALL_GLOBAL_CONFIGS_FAILURE,
      payload: error.message
    });
    throw error;
  }
};
